import React, { useEffect, useState, useRef } from "react";
import { TabContext, TabPanel } from "@mui/lab";
import { Tabs, Tab, Grid2, IconButton, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EnquiryList from "./EnquiryList";
import NewEnquiryForm from "./NewEnquiryForm";
import EditEnquiry from "./EditEnquiry";

const EnquiryHome = () => {
  const enquiryListRef = useRef();

  const handleEditEnquiryTab = (enquiry) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find(
        (tab) => tab.value === `EditEnquiry-${enquiry.id}`
      );

      if (existingTab) {
        setEnquiryActiveTab(existingTab.value);
        return prevTabs;
      }

      const newTab = {
        label: `Edit ${enquiry.name} `,
        value: `EditEnquiry-${enquiry.id}`,
        type: "EditEnquiry",
        enquiryId: enquiry?.id,
      };

      setEnquiryActiveTab(newTab.value);
      return [...prevTabs, newTab];
    });
  };

  const handleSaveEdit = (updatedEnquiry) => {
    // Close the edit tab
    const updatedTabs = tabs.filter(
      (tab) => !tab.value.startsWith("EditEnquiry")
    );
    setTabs(updatedTabs);

    // Switch back to the patient details tab
    const enquiryDetailsTab = tabs.find(
      (tab) => tab.value === `enquiry-${updatedEnquiry.id}`
    );
    if (enquiryDetailsTab) {
      setEnquiryActiveTab(enquiryDetailsTab.value);
    } else {
      setEnquiryActiveTab("Enquiries");
    }
  };

  // Called when a new enquiry is successfully added
  const onNewEnquiryAdded = (tempNewEnquiryId) => {
    // Close the NewEnquiry tab
    setTabs((prevTabs) =>
      prevTabs.filter((tab) => tab.value !== tempNewEnquiryId)
    );
    // Switch back to the "Enquiries" tab
    setEnquiryActiveTab("Enquiries");
    //  Trigger refresh of enquiry list using the ref
    enquiryListRef?.current?.refreshEnquiryList();
  };
  //  Close a tab, but don't allow closing the main "Enquiries" tab
  const handleCloseTab = (tabValue) => {
    if (tabValue === "Enquiries") return;
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.value !== tabValue));
    // If closing current tab, revert to "Enquiries"
    if (EnquiryactiveTab === tabValue) {
      setEnquiryActiveTab("Enquiries");
    }
  };
  //  When a user clicks a different tab
  const handleTabChange = (event, newValue) => {
    setEnquiryActiveTab(newValue);
  };
  //  Create a new tab for adding an enquiry
  const handleNewEnquiryTab = () => {
    const newTabNumber = newEnquiryTabNumber + 1;
    setnewEnquiryTabNumber(newTabNumber);

    const newTab = {
      label: `New Enquiry (${newTabNumber})`,
      value: `NewEnquiry-${newTabNumber}`,
      type: "NewEnquiry",
      tempNewEnquiryId: `NewEnquiry-${newTabNumber}`,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setEnquiryActiveTab(newTab.value);
  };

  // State Initialization
  //  Load active tab from localStorage (or default to "Enquiries")
  const [EnquiryactiveTab, setEnquiryActiveTab] = useState(() => {
    const savedActiveTab = localStorage.getItem("EnquiryactiveTab");
    return savedActiveTab || "Enquiries"; // Default to "Enquiries" if no saved state
  });

  const [tabs, setTabs] = React.useState(() => {
    // Load tabs from local storage
    const savedTabs = localStorage.getItem("Enquiry-tabs");
    return savedTabs
      ? JSON.parse(savedTabs)
      : [{ label: "Enquiries", value: "Enquiries" }];
  });

  const [newEnquiryTabNumber, setnewEnquiryTabNumber] = useState(0);

  // useEffect Hooks
  useEffect(() => {
    localStorage.setItem("Enquiry-tabs", JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    localStorage.setItem("EnquiryactiveTab", EnquiryactiveTab);
  }, [EnquiryactiveTab]);

  return (
    <>
      <Grid2 container>
        <TabContext value={EnquiryactiveTab}>
          <Grid2
            container
            alignItems="center"
            spacing={1}
            sx={{ borderBottom: "1px solid lightgray", width: "100%" }}
          >
            <Tabs
              value={EnquiryactiveTab}
              onChange={handleTabChange}
              variant="scrollable"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  label={
                    <Box>
                      {tab.label}
                      {tab.value !== "Enquiries" && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseTab(tab.value);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  }
                  value={tab.value}
                />
              ))}
            </Tabs>
          </Grid2>

          <Grid2 item size={12}>
            {tabs.map((tab) => (
              <TabPanel
                key={tab.value}
                value={tab.value}
                keepMounted={tab.value !== "Enquiries"}
                sx={{ padding: "0px", width: "100%" }}
              >
                {/*  Default Enquiry List tab */}
                {tab.value === "Enquiries" && (
                  <EnquiryList
                    ref={enquiryListRef}
                    onNewEnquiry={handleNewEnquiryTab}
                    onEditEnquiry={handleEditEnquiryTab}
                  />
                )}
                {/*  New Enquiry form tab */}
                {tab.type === "NewEnquiry" && (
                  <NewEnquiryForm
                    tempNewEnquiryId={tab.tempNewEnquiryId}
                    onNewEnquiryAdded={onNewEnquiryAdded}
                  />
                )}
                {/* edit enquiry tab */}
                {tab.type === "EditEnquiry" && (
                  <EditEnquiry enquiryId={tab.enquiryId} onSave={handleSaveEdit} />
                )}
                {tab.content && React.isValidElement(tab.content)
                  ? tab.content
                  : null}
              </TabPanel>
            ))}
          </Grid2>
        </TabContext>
      </Grid2>
    </>
  );
};

export default EnquiryHome;
